'use client'
import React, { useState, useRef, useEffect } from 'react'
import styles from './Watchlist.module.css'
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

/**
 * @uiComponent
 * @name FavoritesPopup
 * @description A modal popup component for adding stock symbols to the favorites watchlist on Zerodha.
 * Features real-time search with autocomplete suggestions, keyboard navigation, and displays
 * current favorites. Supports both manual symbol entry and selection from suggested stock symbols.
 * @location Overlay popup triggered from the Watchlist component
 * @prop isOpen Boolean controlling the visibility of the popup
 * @prop onClose Callback function to close the popup
 * @prop onAddFavorite Callback function to add a symbol to favorites
 * @prop currentFavorites Array of currently favorited stock symbols
 * @prop availableSymbols Optional array of available stock symbols for autocomplete
 */

/**
 * @uiWorkflow
 * @name Add Stock Symbols to Favorites Using Popup on Zerodha
 * @description How to use the favorites popup to quickly add multiple stock symbols to your watchlist
 * @platform zerodha
 * @step 1 Click the "+ Add" button in the Watchlist component [component: Watchlist] [action: click]
 * @step 2 The favorites popup will open with a search input [component: FavoritesPopup] [action: observe]
 * @step 3 Start typing a stock symbol in the search field [component: FavoritesPopup] [action: input] [notes: e.g., RELIANCE, TCS, INFY]
 * @step 4 Select from the autocomplete suggestions that appear [component: FavoritesPopup] [action: click] [notes: Use arrow keys for keyboard navigation]
 * @step 5 Alternatively, type the complete symbol and click "Add" [component: FavoritesPopup] [action: click]
 * @step 6 Review your current favorites list displayed in the popup [component: FavoritesPopup] [action: observe]
 * @step 7 Add more stock symbols by repeating the search process [component: FavoritesPopup] [action: repeat]
 * @step 8 Close the popup when finished adding symbols [component: FavoritesPopup] [action: click]
 */

/**
 * @uiWorkflow
 * @name Use Keyboard Navigation in Stock Favorites Popup on Zerodha
 * @description How to efficiently navigate and select stock symbols using keyboard shortcuts
 * @platform zerodha
 * @step 1 Open the favorites popup from the watchlist [component: FavoritesPopup] [action: open]
 * @step 2 Start typing a stock symbol to see suggestions [component: FavoritesPopup] [action: input]
 * @step 3 Use the down arrow key to move through suggestions [component: FavoritesPopup] [action: keypress] [notes: Arrow Down key]
 * @step 4 Use the up arrow key to move back up the list [component: FavoritesPopup] [action: keypress] [notes: Arrow Up key]
 * @step 5 Press Enter to select the highlighted suggestion [component: FavoritesPopup] [action: keypress] [notes: Enter key]
 * @step 6 Press Escape to close the popup without adding [component: FavoritesPopup] [action: keypress] [notes: Escape key]
 * @step 7 The selected stock symbol will be added to your favorites [component: FavoritesPopup] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Search and Add Custom Stock Symbols on Zerodha
 * @description How to add stock symbols that may not appear in the autocomplete suggestions
 * @platform zerodha
 * @step 1 Open the favorites popup by clicking "+ Add" [component: FavoritesPopup] [action: click]
 * @step 2 Type the exact stock symbol you want to add [component: FavoritesPopup] [action: input] [notes: e.g., TATAMOTORS, BAJFINANCE, WIPRO]
 * @step 3 If no suggestions appear, you'll see a "no matching symbols" message [component: FavoritesPopup] [action: observe]
 * @step 4 Click the "Add" button to manually add the stock symbol [component: FavoritesPopup] [action: click]
 * @step 5 The symbol will be added to your favorites even without autocomplete [component: FavoritesPopup] [action: observe]
 * @step 6 Check your current favorites list to confirm it was added [component: FavoritesPopup] [action: observe]
 * @step 7 Close the popup and verify the symbol appears in your watchlist [component: Watchlist] [action: observe]
 */

interface FavoritesPopupProps {
  isOpen: boolean
  onClose: () => void
  onAddFavorite: (symbol: string) => void
  currentFavorites: string[]
  availableSymbols?: string[] // Optional list of available symbols
}

export function FavoritesPopup({
  isOpen,
  onClose,
  onAddFavorite,
  currentFavorites,
  availableSymbols = []
}: FavoritesPopupProps) {
  const [symbol, setSymbol] = useState('')
  const [filteredSymbols, setFilteredSymbols] = useState<string[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter available symbols based on input
  useEffect(() => {
    if (symbol.trim() === '') {
      setFilteredSymbols([])
      setSelectedIndex(-1)
      return
    }

    const upperSymbol = symbol.toUpperCase()
    const filtered = availableSymbols
      .filter(s => s.includes(upperSymbol) && !currentFavorites.includes(s))
      .slice(0, 10) // Limit to 10 results

    setFilteredSymbols(filtered)
    setSelectedIndex(filtered.length > 0 ? 0 : -1)
  }, [symbol, availableSymbols, currentFavorites])

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Focus the input when popup opens
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredSymbols.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev =>
          prev < filteredSymbols.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        if (selectedIndex >= 0) {
          handleSymbolSelect(filteredSymbols[selectedIndex])
        } else if (symbol.trim()) {
          handleSubmit(e)
        }
        break
      case 'Escape':
        onClose()
        break
    }
  }

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (symbol.trim()) {
      onAddFavorite(symbol.toUpperCase())
      setSymbol('')
    }
  }

  const handleSymbolSelect = (selected: string) => {
    onAddFavorite(selected)
    setSymbol('')
  }

  return (
    <div className={styles.popupOverlay}>
      <div ref={popupRef} className={styles.popupContent}>
        <div className={styles.popupHeader}>
          <h3>Add Favorite Symbol</h3>
          <button
            onClick={onClose}
            className={styles.closeIconButton}
            aria-label="Close"
          >
            <XMarkIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.popupBody}>
          <form onSubmit={handleSubmit} className={styles.addForm}>
            <div className={styles.searchInputContainer}>
              <MagnifyingGlassIcon
                className={styles.popupSearchIcon}
                width={18}
                height={18}
              />
              <input
                ref={inputRef}
                type="text"
                value={symbol}
                onChange={e => setSymbol(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search or enter symbol (e.g. BTCUSDT)"
                className={styles.popupInput}
              />
            </div>
            <button type="submit" className={styles.addSymbolButton}>
              Add
            </button>
          </form>

          {filteredSymbols.length > 0 && (
            <div className={styles.suggestionsList}>
              {filteredSymbols.map((sym, index) => (
                <div
                  key={sym}
                  className={`${styles.suggestionItem} ${index === selectedIndex ? styles.selected : ''}`}
                  onClick={() => handleSymbolSelect(sym)}
                >
                  {sym}
                </div>
              ))}
            </div>
          )}

          {symbol && filteredSymbols.length === 0 && (
            <div className={styles.noResults}>
              No matching symbols found. You can still add "
              {symbol.toUpperCase()}" manually.
            </div>
          )}

          {currentFavorites.length > 0 && (
            <div className={styles.currentFavoritesSection}>
              <h4>Current Favorites</h4>
              <div className={styles.favoritesList}>
                {currentFavorites.map(fav => (
                  <div key={fav} className={styles.favoriteTag}>
                    {fav}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.popupFooter}>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
